import React, { useMemo } from 'react';
import { Card, CardBody, Badge, UncontrolledTooltip, Progress } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate hook
import VulnerabilityDashboard from './sbomreportnew';

const StatCard = ({ title, value, icon, color, subtitle = null }) => (
    <Card className="shadow-sm d-flex flex-column h-100">
        <div className={`position-relative`}>
            <div className={`position-absolute top-0 start-0 w-2vh`} style={{ backgroundColor: `#${color}` }} />
            <CardBody className="d-flex flex-column justify-content-between">
                <div className="d-flex align-items-center">
                    <div className={`rounded-circle p-3 bg-${color}-100`}>
                        <i className={`bx ${icon} text-${color}-500`} style={{ fontSize: '24px' }} />
                    </div>
                    <div className="ms-3">
                        <h3 className="mb-0 fw-semibold">{value}</h3>
                        <p className="text-muted mb-0">{title}</p>
                        {subtitle && <small className="text-muted">{subtitle}</small>}
                    </div>
                </div>
            </CardBody>
        </div>
    </Card>
);

const VulnerabilitySummary = ({ data }) => {
    const navigate = useNavigate(); // Initialize navigate hook
    const dataToUse = data.analysis;

    // Calculate statistics
    const stats = useMemo(() => {
        if (!dataToUse?.matches) return {};

        const vulnerabilities = dataToUse.matches;
        const severityCounts = vulnerabilities.reduce((acc, v) => {
            const severity = v.vulnerability.severity?.toLowerCase();
            acc[severity] = (acc[severity] || 0) + 1;
            return acc;
        }, {});

        const fixableCount = vulnerabilities.filter(v =>
            v.vulnerability.fix?.versions?.length > 0
        ).length;

        const criticalAndHighCount = (severityCounts.critical || 0) + (severityCounts.high || 0);

        const uniquePackages = new Set(vulnerabilities.map(v => v.artifact.name));

        return {
            total: vulnerabilities.length,
            criticalAndHigh: criticalAndHighCount,
            fixable: fixableCount,
            packagesAffected: uniquePackages.size,
            severityCounts
        };
    }, [dataToUse]);

    const columns = useMemo(
        () => [
            {
                header: "Vulnerability",
                cell: (info) => (
                    <div>
                        <Link to={`/vulnerability/${info.row.original.vulnerability.id}`} className="text-primary fw-semibold">
                            {info.row.original.vulnerability.id}
                        </Link>
                        <div className="text-muted small mt-1">
                            {info.row.original.vulnerability.description?.slice(0, 100)}
                            {info.row.original.vulnerability.description?.length > 100 ? '...' : ''}
                        </div>
                    </div>
                ),
            },
            {
                header: "Package",
                cell: (info) => (
                    <div>
                        <div className="fw-semibold">{info.row.original.artifact.name}</div>
                        <div className="text-muted small">
                            Current: {info.row.original.artifact.version}
                            {info.row.original.vulnerability.fix?.versions?.[0] && (
                                <span className="text-success ms-2">
                                    → {info.row.original.vulnerability.fix.versions[0]}
                                </span>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                header: "Risk Level",
                cell: (info) => {
                    const severity = info.row.original.vulnerability.severity?.toLowerCase();
                    const cvssScore = info.row.original.vulnerability.cvss?.[0]?.metrics?.baseScore;

                    return (
                        <div>
                            <div>
                                <Badge
                                    color={severity === 'critical' ? 'danger' :
                                        severity === 'high' ? 'orange' :
                                            severity === 'medium' ? 'warning' : 'info'
                                    }
                                    className="px-3 py-2"
                                >
                                    {severity?.toUpperCase()}
                                </Badge>
                            </div>
                            {cvssScore && (
                                <div className="text-muted small mt-1">
                                    CVSS: {cvssScore.toFixed(1)}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                header: "Fix Status",
                cell: (info) => {
                    const fixVersion = info.row.original.vulnerability.fix?.versions?.[0];
                    return (
                        <div>
                            {fixVersion ? (
                                <div className="text-success">
                                    <i className="bx bx-check me-1" />
                                    Fix Available
                                </div>
                            ) : (
                                <div className="text-warning">
                                    <i className="bx bx-error me-1" />
                                    No Fix Available
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                header: "Actions",
                cell: (info) => {
                    const handleClick = () => {
                        // Passing data as state while navigating
                        navigate(`/details/${info.row.original.vulnerability.id}`, {
                            state: { data: info.row.original }
                        });
                    };

                    return (
                        <div className="d-flex gap-2">
                            <button
                                onClick={handleClick}
                                id={`view-${info.row.original.vulnerability.id}`}
                                className="btn btn-sm btn-soft-primary"
                            >
                                <i className="mdi mdi-eye-outline" />
                            </button>
                            <UncontrolledTooltip target={`view-${info.row.original.vulnerability.id}`}>
                                View Full Details
                            </UncontrolledTooltip>
                        </div>
                    );
                },
            }
        ],
        [navigate]
    );

    if (!dataToUse?.matches) {
        return <div>No vulnerability data available</div>;
    }

    const riskDistribution =
        (stats.severityCounts.critical || 0) +
        (stats.severityCounts.high || 0) +
        (stats.severityCounts.medium || 0) +
        (stats.severityCounts.low || 0);

    return (
        <div className="container-fluid">
            {/* Statistics Cards */}
            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <StatCard
                        title="Total Vulnerabilities"
                        value={stats.total}
                        icon="bx-shield text-primary"
                        subtitle={`Across ${stats.packagesAffected} packages`}
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        title="Critical & High"
                        value={stats.criticalAndHigh}
                        icon="bx-error text-danger"
                        subtitle="Require immediate attention"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        title="Fixable"
                        value={stats.fixable}
                        icon="bx-check text-success"
                        subtitle="Updates available"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        title="Affected Packages"
                        value={stats.packagesAffected}
                        icon="bx-package text-info"
                        color="purple"
                    />
                </div>
            </div>

            {/* Risk Distribution Progress Bar */}
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">Risk Level Distribution</h5>
                    </div>
                    <Progress multi style={{ height: "1.5rem" }}>
                        <Progress bar color="danger" value={(stats.severityCounts.critical || 0) / riskDistribution * 100} >{stats.severityCounts.critical}</Progress>
                        <Progress bar color="orange" value={(stats.severityCounts.high || 0) / riskDistribution * 100} >{stats.severityCounts.high}</Progress>
                        <Progress bar color="warning" value={(stats.severityCounts.medium || 0) / riskDistribution * 100} >{stats.severityCounts.medium}</Progress>
                        <Progress bar color="info" value={(stats.severityCounts.low || 0) / riskDistribution * 100} >{stats.severityCounts.low}</Progress>
                    </Progress>
                </CardBody>
            </Card>

            {/* Vulnerabilities Table */}
            <Card className="shadow-sm">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">Vulnerability Details</h5>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="bg-light">
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column.header} className="border-0">
                                            {column.header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dataToUse.matches.map((row, index) => (
                                    <tr key={index}>
                                        {columns.map((column) => (
                                            <td key={column.header}>
                                                {column.cell({ row: { original: row } })}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default VulnerabilitySummary;
